/**
 * Review & Finalize Step
 * 
 * Required step before dispute package generation.
 * Includes:
 * 1. Dispute package review
 * 2. Optional supporting document upload
 * 3. Required attestation checkbox
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight,
  ArrowLeft,
  FileText,
  Upload,
  Check,
  AlertTriangle,
  Eye,
  Download,
  Trash2,
  Plus,
  Shield,
  CheckCircle2,
  Building2,
  Loader2,
  Info,
  Lock,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

// Bureau colors
const BUREAU_COLORS = {
  TransUnion: 'border-emerald-500 bg-emerald-500/10',
  Experian: 'border-amber-500 bg-amber-500/10',
  Equifax: 'border-red-500 bg-red-500/10',
};

// Supporting document types
const DOCUMENT_TYPES = [
  { id: 'identity_theft_affidavit', label: 'Identity Theft Affidavit', icon: Shield },
  { id: 'police_report', label: 'Police Report', icon: FileText },
  { id: 'account_statement', label: 'Account Statement', icon: FileText },
  { id: 'settlement_letter', label: 'Settlement Letter', icon: FileText },
  { id: 'proof_of_payment', label: 'Proof of Payment', icon: FileText },
  { id: 'proof_of_address', label: 'Proof of Address', icon: FileText },
  { id: 'id_document', label: 'ID Document', icon: Shield },
  { id: 'breach_notice', label: 'Breach Notice', icon: AlertTriangle },
  { id: 'other', label: 'Other Document', icon: FileText },
];

// Dispute item summary card
const DisputeItemSummary = ({ item }) => (
  <div className="flex items-center justify-between p-3 bg-stone-800/50 rounded-lg border border-slate-700">
    <div className="flex items-center gap-3">
      <div className="w-8 h-8 rounded-lg bg-slate-700 flex items-center justify-center">
        <FileText size={16} className="text-slate-400" />
      </div>
      <div>
        <p className="text-white text-sm font-medium">{item.creditor_name || item.name}</p>
        <p className="text-slate-400 text-xs">{item.type} • {item.bureau}</p>
      </div>
    </div>
    <Badge variant="outline" className="text-xs">
      {item.issue_type || 'Disputed'}
    </Badge>
  </div>
);

// Document upload card
const DocumentUploadCard = ({ document, onRemove }) => (
  <div className="flex items-center justify-between p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
    <div className="flex items-center gap-3">
      <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
        <FileText size={16} className="text-emerald-400" />
      </div>
      <div>
        <p className="text-white text-sm font-medium truncate max-w-[200px]">{document.name}</p>
        <p className="text-slate-400 text-xs">{document.type} • {(document.size / 1024).toFixed(1)} KB</p>
      </div>
    </div>
    <Button
      variant="ghost"
      size="sm"
      onClick={() => onRemove(document.id)}
      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
    >
      <Trash2 size={14} />
    </Button>
  </div>
);

// Letter preview section
const LetterPreview = ({ letterContent, bureau, onPreview }) => (
  <div className={`border rounded-xl overflow-hidden ${BUREAU_COLORS[bureau] || 'border-slate-700 bg-stone-800/50'}`}>
    <div className="p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Building2 size={18} className="text-slate-400" />
          <h4 className="font-medium text-white">{bureau} Letter</h4>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onPreview(bureau)}
          className="text-slate-400 hover:text-white"
        >
          <Eye size={14} className="mr-1" />
          Preview
        </Button>
      </div>
      
      {letterContent ? (
        <div className="p-3 bg-stone-900/50 rounded-lg text-sm text-slate-300 max-h-32 overflow-hidden">
          <p className="line-clamp-4">{letterContent.substring(0, 300)}...</p>
        </div>
      ) : (
        <div className="p-3 bg-stone-900/50 rounded-lg text-sm text-slate-400 text-center">
          Letter will be generated upon finalization
        </div>
      )}
    </div>
  </div>
);

// Main Review & Finalize component
const ReviewFinalizeStep = ({
  disputeType, // 'factual' or 'metro2'
  disputeSubType, // For factual: 'general', 'identity_theft', 'data_breach'
  selectedItems,
  itemDetails,
  packages,
  letterPreviews,
  onUploadDocument,
  onRemoveDocument,
  uploadedDocuments = [],
  onFinalize,
  onBack,
  isLoading = false,
  simplified = false, // Auto Starter mode
}) => {
  const [attestationChecked, setAttestationChecked] = useState(false);
  const [showUploadSection, setShowUploadSection] = useState(false);
  const [previewBureau, setPreviewBureau] = useState(null);
  
  // File upload handler
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    for (const file of files) {
      // Validate file size (25MB max)
      if (file.size > 25 * 1024 * 1024) {
        toast.error(`${file.name} is too large. Maximum size is 25MB.`);
        continue;
      }
      
      // Validate file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast.error(`${file.name} is not a supported file type.`);
        continue;
      }
      
      await onUploadDocument(file);
    }
    
    // Reset input
    e.target.value = '';
  };
  
  const handleFinalize = () => {
    if (!attestationChecked) {
      toast.error('Please confirm the attestation before finalizing.');
      return;
    }
    onFinalize();
  };
  
  // Get bureaus from packages
  const bureaus = packages?.map(p => p.bureau) || ['TransUnion', 'Experian', 'Equifax'];
  
  return (
    <div className="min-h-screen bg-stone-900 py-8 px-4" data-testid="review-finalize-step">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Button variant="ghost" onClick={onBack} className="mb-4">
            <ArrowLeft size={18} className="mr-1" />
            Back
          </Button>
          
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
              <CheckCircle2 size={24} className="text-emerald-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">Review & Finalize</h1>
              <p className="text-slate-400">Review your dispute package before generation</p>
            </div>
          </div>
        </div>
        
        {/* Summary Cards */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <div className="bg-stone-800/50 border border-slate-700 rounded-xl p-4">
            <p className="text-slate-400 text-sm mb-1">Dispute Type</p>
            <p className="text-white font-medium capitalize">
              {disputeType === 'factual' ? 'Factual Dispute' : 'Metro 2 Dispute'}
            </p>
            {disputeSubType && (
              <p className="text-slate-400 text-xs mt-1 capitalize">
                {disputeSubType.replace(/_/g, ' ')}
              </p>
            )}
          </div>
          <div className="bg-stone-800/50 border border-slate-700 rounded-xl p-4">
            <p className="text-slate-400 text-sm mb-1">Items Included</p>
            <p className="text-2xl font-bold text-white">{selectedItems?.length || 0}</p>
          </div>
          <div className="bg-stone-800/50 border border-slate-700 rounded-xl p-4">
            <p className="text-slate-400 text-sm mb-1">Letters to Generate</p>
            <p className="text-2xl font-bold text-white">{bureaus.length}</p>
          </div>
        </div>
        
        {/* Section 1: Disputed Items Review */}
        <section className="mb-8">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <FileText size={20} className="text-emerald-400" />
            Disputed Items
          </h2>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {selectedItems?.map((item) => (
              <DisputeItemSummary key={item.id} item={item} />
            ))}
            {(!selectedItems || selectedItems.length === 0) && (
              <div className="text-center py-8 text-slate-400">
                No items selected for dispute
              </div>
            )}
          </div>
        </section>
        
        {/* Section 2: Letter Previews */}
        <section className="mb-8">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Building2 size={20} className="text-emerald-400" />
            Generated Letters Preview
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            {bureaus.map((bureau) => (
              <LetterPreview
                key={bureau}
                bureau={bureau}
                letterContent={letterPreviews?.[bureau]}
                onPreview={setPreviewBureau}
              />
            ))}
          </div>
        </section>
        
        {/* Section 3: Supporting Documents (Optional) */}
        <section className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Upload size={20} className="text-amber-400" />
              Supporting Documents
              <Badge variant="outline" className="ml-2">Optional</Badge>
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowUploadSection(!showUploadSection)}
              className="text-slate-400 hover:text-white"
            >
              {showUploadSection ? 'Hide' : 'Add Documents'}
            </Button>
          </div>
          
          {/* Uploaded documents list */}
          {uploadedDocuments.length > 0 && (
            <div className="space-y-2 mb-4">
              {uploadedDocuments.map((doc) => (
                <DocumentUploadCard
                  key={doc.id}
                  document={doc}
                  onRemove={onRemoveDocument}
                />
              ))}
            </div>
          )}
          
          {/* Upload section */}
          <AnimatePresence>
            {showUploadSection && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden"
              >
                <div className="p-4 bg-stone-800/50 border border-slate-700 rounded-xl">
                  <p className="text-slate-300 text-sm mb-4">
                    Upload any supporting documents that may strengthen your dispute. These are optional but recommended when relevant.
                  </p>
                  
                  {/* Document type suggestions */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {DOCUMENT_TYPES.slice(0, 6).map((type) => {
                      const Icon = type.icon;
                      return (
                        <span
                          key={type.id}
                          className="text-xs px-2 py-1 rounded bg-slate-700 text-slate-400 flex items-center gap-1"
                        >
                          <Icon size={12} />
                          {type.label}
                        </span>
                      );
                    })}
                  </div>
                  
                  {/* Upload area */}
                  <label className="block cursor-pointer">
                    <div className="border-2 border-dashed border-slate-600 hover:border-slate-500 rounded-xl p-6 text-center transition-colors">
                      <Upload size={24} className="mx-auto text-slate-500 mb-2" />
                      <p className="text-slate-300 text-sm mb-1">
                        Drop files here or click to upload
                      </p>
                      <p className="text-slate-500 text-xs">
                        PDF, JPEG, PNG up to 25MB
                      </p>
                    </div>
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.jpg,.jpeg,.png,.webp"
                      onChange={handleFileUpload}
                      className="hidden"
                      data-testid="document-upload-input"
                    />
                  </label>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          
          {uploadedDocuments.length === 0 && !showUploadSection && (
            <p className="text-slate-500 text-sm">
              No supporting documents uploaded. Click "Add Documents" to strengthen your dispute.
            </p>
          )}
        </section>
        
        {/* Section 4: Required Attestation */}
        <section className="mb-8">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Shield size={20} className="text-red-400" />
            Required Attestation
          </h2>
          
          <div className={`p-6 rounded-xl border-2 transition-all ${
            attestationChecked 
              ? 'border-emerald-500 bg-emerald-500/10' 
              : 'border-slate-700 bg-stone-800/50'
          }`}>
            <div className="flex items-start gap-4">
              <Checkbox
                id="attestation"
                checked={attestationChecked}
                onCheckedChange={setAttestationChecked}
                className="mt-1"
                data-testid="attestation-checkbox"
              />
              <div className="flex-1">
                <Label 
                  htmlFor="attestation" 
                  className="text-white cursor-pointer leading-relaxed"
                >
                  I confirm that the information I provided is true and accurate to the best of my knowledge, and that any documents I uploaded relate to my dispute and may be used in preparing my dispute package.
                </Label>
              </div>
            </div>
            
            {!attestationChecked && (
              <div className="mt-4 flex items-center gap-2 text-amber-400 text-sm">
                <AlertTriangle size={16} />
                <span>You must check this box to continue</span>
              </div>
            )}
          </div>
        </section>
        
        {/* Info Note */}
        <div className="mb-8 p-4 bg-stone-800/50 border border-slate-700 rounded-xl">
          <div className="flex items-start gap-3">
            <Info size={20} className="text-emerald-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-slate-400">
              <p className="text-white font-medium mb-1">What happens next?</p>
              <p>
                After you finalize, we will generate your dispute letters. You can then download them, 
                track their status, and upload any response documents you receive from the bureaus.
              </p>
            </div>
          </div>
        </div>
        
        {/* Navigation */}
        <div className="flex justify-between items-center sticky bottom-4 bg-stone-900/95 backdrop-blur p-4 rounded-xl border border-slate-700">
          <div className="text-sm text-slate-400">
            {attestationChecked ? (
              <span className="text-emerald-400 flex items-center gap-1">
                <Check size={16} />
                Ready to finalize
              </span>
            ) : (
              <span className="text-amber-400 flex items-center gap-1">
                <Lock size={16} />
                Complete attestation to continue
              </span>
            )}
          </div>
          
          <Button
            onClick={handleFinalize}
            disabled={!attestationChecked || isLoading}
            className={`${
              attestationChecked 
                ? 'bg-emerald-600 hover:bg-emerald-700' 
                : 'bg-slate-600 cursor-not-allowed'
            }`}
            data-testid="finalize-btn"
          >
            {isLoading ? (
              <>
                <Loader2 size={18} className="animate-spin mr-1" />
                Generating...
              </>
            ) : (
              <>
                Generate Dispute Package
                <ArrowRight size={18} className="ml-1" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReviewFinalizeStep;
